import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { updateItem } from "../api";
import { useItemsWithAuth } from "../hooks/useItemsWithAuth";
import { Category, ShoppingListItem } from "../types";
import { buildFaviconUrl, normalizeLinks } from "../utils/linkHelpers";
import { PlusIcon, CollapseIcon, ExpandIcon } from "./Icons";

const categoryLabels: Record<Category, string> = {
  toy: "Toy",
  clothing: "Clothing",
  consumable: "Consumable",
  home: "Home"
};

export default function ItemAppendPage() {
  const { items, setItems, loading, error, setError } = useItemsWithAuth();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [attachingId, setAttachingId] = useState<string | null>(null);

  const attachUrl = searchParams.get("attachUrl") ?? "";
  const attachName = searchParams.get("attachName") ?? "";

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const list = term ? items.filter((item) => item.name.toLowerCase().includes(term)) : items;
    return [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [items, filter]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAttach = async (item: ShoppingListItem) => {
    if (!attachUrl) {
      setError("No attachUrl provided.");
      return;
    }
    if (attachingId) return;
    setAttachingId(item.id);
    setError(null);
    const existingUrls = Array.isArray(item.urls) ? item.urls : item.url ? [item.url] : [];
    const nextUrls = Array.from(new Set([attachUrl, ...existingUrls].filter(Boolean)));
    try {
      const updated = await updateItem(item.id, {
        category: item.category,
        name: item.name,
        description: item.description ?? "",
        urls: nextUrls,
        url: nextUrls[0] ?? "",
        reference: Array.isArray(item.reference) ? item.reference : [],
        got: item.got,
        quantity: item.quantity
      });
      setItems((prev) => prev.map((entry) => (entry.id === item.id ? { ...entry, ...updated } : entry)));
    } catch (err) {
      setError((err as Error).message ?? "Unable to attach URL");
    } finally {
      setAttachingId(null);
    }
  };

  if (!attachUrl && !loading) {
    return (
      <div className="frosted-card">
        <div className="page-actions">
          <Link to="/" className="link-button">
            ← Back to list
          </Link>
        </div>
        <p className="error">attachUrl is required. Use the Tampermonkey helper or supply ?attachUrl=...</p>
      </div>
    );
  }

  if (loading) {
    return <div>Loading items…</div>;
  }

  if (error === "signin-required") {
    return <div className="error">Redirecting to login…</div>;
  }

  if (error) {
    return (
      <div className="error">
        <div className="page-actions">
          <Link to="/" className="link-button">
            ← Back to list
          </Link>
        </div>
        {error}
      </div>
    );
  }

  return (
    <div className="frosted-card">
      <div className="page-actions" style={{ justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
        <Link to="/" className="link-button" title="Back to main list">
          ← Back to list
        </Link>
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Search items…"
          style={{ padding: "0.5rem 0.75rem", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.12)", minWidth: "240px" }}
          aria-label="Search items"
          title="Search items"
        />
      </div>
      {attachName && (
        <div className="info" style={{ marginBottom: "0.75rem" }}>
          Attaching: <strong>{attachName}</strong>
        </div>
      )}
      {filtered.length === 0 ? (
        <p>No items found.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {filtered.map((item) => {
            const urls = normalizeLinks(item);
            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 0.5rem",
                  borderBottom: "1px solid rgba(0,0,0,0.08)"
                }}
              >
                <button
                  type="button"
                  className="link-button icon-button"
                  onClick={() => handleAttach(item)}
                  disabled={attachingId === item.id}
                  aria-label="Attach URL to this item"
                  title="Attach URL to this item"
                >
                  {attachingId === item.id ? "…" : <PlusIcon ariaHidden />}
                </button>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                      <span className="chip" title={categoryLabels[item.category]}>
                        {categoryLabels[item.category]}
                      </span>
                      <span className="pill pill-quantity" title="Quantity">
                        {item.quantity}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.35rem", flexWrap: "wrap" }}>
                    {urls.map((link, index) => (
                      <a
                        key={`${item.id}-url-${index}`}
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="pill"
                        style={{ padding: "0.2rem 0.35rem", display: "inline-flex", alignItems: "center" }}
                        aria-label={urls.length > 1 ? `Open URL ${index + 1}` : "Open URL"}
                        title={urls.length > 1 ? `Open URL ${index + 1}` : "Open URL"}
                      >
                        <img
                          src={buildFaviconUrl(link)}
                          alt=""
                          aria-hidden
                          width={16}
                          height={16}
                          style={{ borderRadius: "4px", objectFit: "contain" }}
                          referrerPolicy="no-referrer"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                        <span className="sr-only">{urls.length > 1 ? `Open URL ${index + 1}` : "Open URL"}</span>
                      </a>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className="link-button icon-button"
                  onClick={() => toggleExpanded(item.id)}
                  aria-label={expanded.has(item.id) ? "Hide details" : "Show details"}
                  title={expanded.has(item.id) ? "Hide details" : "Show details"}
                >
                  {expanded.has(item.id) ? <CollapseIcon ariaHidden /> : <ExpandIcon ariaHidden />}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: "0.75rem", color: "#555", fontSize: "0.9rem" }}>
        Tip: Use the Tampermonkey “Add to existing” button to open this page with the current product URL pre-filled.
      </div>
    </div>
  );
}
